import { useCallback, useEffect, useState } from 'react';
import {
    resolveMediaBlob,
    resolveMediaThumb,
    type MediaBlobProgress,
    type MediaBlobResult,
    type ResolveMediaOptions
} from './loadMediaBlob';

export interface MediaBlobUrl {
    /** Object URL for the image, or null if not yet displayable. */
    url: string | null;
    /** Still resolving (cache miss + fetch in flight). */
    loading: boolean;
    /** Binary not available yet — the origin device hasn't uploaded it. */
    pending: boolean;
    /** How far the download from the server has come, while there is one. */
    progress: MediaBlobProgress | null;
}

/**
 * Progress is handed on in whole percent: a 4 MB photo arrives in hundreds of
 * chunks, and a render for each one would cost the phone more than the bar is
 * worth.
 */
function sameStep(
    previous: MediaBlobProgress | null,
    next: MediaBlobProgress
): boolean {
    if (!previous || previous.total !== next.total) {
        return false;
    }
    const step = next.total ? next.total / 100 : 64 * 1024;
    return (
        Math.floor(previous.loaded / step) === Math.floor(next.loaded / step)
    );
}

/**
 * Resolve a displayable object URL for a media id, offline-first — see
 * {@link resolveMediaBlob} for the lookup order. The URL is revoked when the id
 * changes or the component unmounts, so callers must not outlive it.
 */
export function useMediaBlobUrl(id: string): MediaBlobUrl {
    return useResolvedUrl(id, resolveMediaBlob);
}

/**
 * The same for a small preview (see {@link resolveMediaThumb}) — what a gallery
 * card or a picker tile shows. Downloading and editing still want the original.
 * `version` is the media doc's `updatedAt`: a new one asks the server again.
 */
export function useMediaThumbUrl(id: string, version: string): MediaBlobUrl {
    const resolve = useCallback(
        (mediaId: string, options: ResolveMediaOptions) =>
            resolveMediaThumb(mediaId, options),
        // Only a trigger: the server, not the version, says what is current.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [version]
    );
    return useResolvedUrl(id, resolve);
}

function useResolvedUrl(
    id: string,
    resolveBlob: (
        id: string,
        options: ResolveMediaOptions
    ) => Promise<MediaBlobResult>
): MediaBlobUrl {
    const [state, setState] = useState<MediaBlobUrl>({
        url: null,
        loading: true,
        pending: false,
        progress: null
    });

    useEffect(() => {
        // Cancels the download too, not just the answer: a closed dialog
        // must not go on taking the line from the next photo.
        const controller = new AbortController();
        let objectUrl: string | null = null;
        let shownBlob: Blob | null = null;

        function show(blob: Blob) {
            // A 304 or a failed request hands back the very picture already
            // shown — a fresh URL for it would only make the image blink.
            if (blob === shownBlob) {
                return;
            }
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
            shownBlob = blob;
            objectUrl = URL.createObjectURL(blob);
            setState({
                url: objectUrl,
                loading: false,
                pending: false,
                progress: null
            });
        }

        async function resolve() {
            setState({
                url: null,
                loading: true,
                pending: false,
                progress: null
            });
            const { blob, pending } = await resolveBlob(id, {
                signal: controller.signal,
                onCached: (cached) => {
                    if (!controller.signal.aborted) {
                        show(cached);
                    }
                },
                onProgress: (progress) =>
                    setState((current) =>
                        sameStep(current.progress, progress)
                            ? current
                            : { ...current, progress }
                    )
            });
            if (controller.signal.aborted) {
                return;
            }
            if (!blob) {
                if (shownBlob) {
                    return;
                }
                setState({
                    url: null,
                    loading: false,
                    pending,
                    progress: null
                });
                return;
            }
            show(blob);
        }

        void resolve();

        return () => {
            controller.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [id, resolveBlob]);

    return state;
}
